import { useEffect } from "react";
import { useLocation } from "react-router-dom";

type StructuredDataValue =
  | boolean
  | null
  | number
  | string
  | StructuredDataObject
  | StructuredDataValue[];

interface StructuredDataObject {
  [key: string]: StructuredDataValue;
}

type StructuredData = StructuredDataObject;

interface PageMetadata {
  title: string;
  description: string;
  robots: string;
  keywords?: string;
  type?: "article" | "website";
  imagePath?: string;
  structuredData?: StructuredData;
}

const DEFAULT_IMAGE_PATH = "/images/arti-og.png";
const STRUCTURED_DATA_ELEMENT_ID = "arti-structured-data";

function upsertMetaTag(
  selector: "name" | "property",
  key: string,
  content: string,
): void {
  let element = document.head.querySelector<HTMLMetaElement>(
    `meta[${selector}="${key}"]`,
  );
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(selector, key);
    document.head.append(element);
  }
  element.content = content;
}

function upsertLinkTag(rel: string, href: string): void {
  let element = document.head.querySelector<HTMLLinkElement>(
    `link[rel="${rel}"]`,
  );
  if (!element) {
    element = document.createElement("link");
    element.rel = rel;
    document.head.append(element);
  }
  element.href = href;
}

function upsertStructuredData(
  structuredData: StructuredData | undefined,
): void {
  const existingElement = document.head.querySelector<HTMLScriptElement>(
    `#${STRUCTURED_DATA_ELEMENT_ID}`,
  );

  if (!structuredData) {
    existingElement?.remove();
    return;
  }

  const nextElement = existingElement ?? document.createElement("script");
  nextElement.id = STRUCTURED_DATA_ELEMENT_ID;
  nextElement.type = "application/ld+json";
  nextElement.textContent = JSON.stringify(structuredData);

  if (!existingElement) {
    document.head.append(nextElement);
  }
}

export function usePageMetadata(metadata: PageMetadata | null): void {
  const location = useLocation();

  useEffect(() => {
    if (!metadata) {
      return;
    }

    const canonicalUrl = new URL(
      location.pathname,
      window.location.origin,
    ).toString();
    const imageUrl = new URL(
      metadata.imagePath ?? DEFAULT_IMAGE_PATH,
      window.location.origin,
    ).toString();

    document.title = metadata.title;

    upsertMetaTag("name", "description", metadata.description);
    upsertMetaTag("name", "robots", metadata.robots);
    upsertMetaTag("name", "twitter:card", "summary_large_image");
    upsertMetaTag("name", "twitter:description", metadata.description);
    upsertMetaTag("name", "twitter:image", imageUrl);
    upsertMetaTag("name", "twitter:title", metadata.title);
    upsertMetaTag("property", "og:description", metadata.description);
    upsertMetaTag("property", "og:image", imageUrl);
    upsertMetaTag(
      "property",
      "og:image:alt",
      "Arti — AI teammates that remember. Spawn once. Keep forever.",
    );
    upsertMetaTag("property", "og:site_name", "Arti");
    upsertMetaTag("property", "og:title", metadata.title);
    upsertMetaTag("property", "og:type", metadata.type ?? "website");
    upsertMetaTag("property", "og:url", canonicalUrl);

    if (metadata.keywords) {
      upsertMetaTag("name", "keywords", metadata.keywords);
    }

    upsertLinkTag("canonical", canonicalUrl);
    upsertStructuredData(metadata.structuredData);
  }, [location.pathname, metadata]);
}
